import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStats, getAlertesStock, getEvolutionVentes, getConfigurations, getTopProduits, getCaisseActive, ouvrirCaisse, fermerCaisse } from '../services/api';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell
} from 'recharts';
import {
  TrendingUp, ShoppingCart, Package, AlertTriangle,
  RefreshCw, Plus, BarChart3, Loader, DollarSign, Warehouse,
  ArrowUpRight, ArrowDownRight, ChevronRight, Zap, Users, FileText,
  Lock, Unlock, X, CheckCircle, AlertCircle, Landmark
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBoutique } from '../context/BoutiqueContext';
import moment from 'moment';
import 'moment/locale/fr';
import './DashboardFeveo.css';

moment.locale('fr');

const BRAND = '#fca311';
const PALETTE = ['#fca311', '#6366f1', '#10b981', '#f43f5e', '#0ea5e9', '#8b5cf6'];

export default function DashboardFeveo() {
  const { user } = useAuth();
  const { selectedBoutiqueId } = useBoutique();
  const navigate = useNavigate();
  const [stats, setStats]       = useState(null);
  const [alertes, setAlertes]   = useState([]);
  const [evolution, setEvolution] = useState([]);
  const [topProduits, setTopProduits] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [timeRange, setTimeRange] = useState('30');
  const [config, setConfig]     = useState({ devise: 'XOF', nom_entreprise: '' });

  // Caisse
  const [caisse,          setCaisse]          = useState(undefined); // undefined=chargement, null=fermée, obj=ouverte
  const [caisseModal,     setCaisseModal]     = useState(null);      // 'ouvrir' | 'fermer'
  const [fondCaisse,      setFondCaisse]      = useState('');
  const [montantCompte,   setMontantCompte]   = useState('');
  const [noteCaisse,      setNoteCaisse]      = useState('');
  const [submittingCaisse,setSubmittingCaisse]= useState(false);
  const [caisseToast,     setCaisseToast]     = useState(null);

  useEffect(() => {
    loadConfigurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCaisse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBoutiqueId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const loadCaisse = async () => {
    try {
      const bid = (selectedBoutiqueId && selectedBoutiqueId !== 'all') ? selectedBoutiqueId : undefined;
      const r = await getCaisseActive(bid);
      setCaisse(r.data.data); // null si pas de caisse ouverte
    } catch { setCaisse(null); }
  };

  const showCaisseToast = (type, text) => {
    setCaisseToast({ type, text });
    setTimeout(() => setCaisseToast(null), 3500);
  };

  const handleOuvrirCaisse = async () => {
    setSubmittingCaisse(true);
    try {
      const payload = { fondCaisse: parseFloat(fondCaisse) || 0, noteOuverture: noteCaisse };
      if (selectedBoutiqueId && selectedBoutiqueId !== 'all') payload.boutiqueId = selectedBoutiqueId;
      await ouvrirCaisse(payload);
      showCaisseToast('success', 'Caisse ouverte avec succès');
      setCaisseModal(null); setFondCaisse(''); setNoteCaisse('');
      await loadCaisse();
    } catch (err) { showCaisseToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmittingCaisse(false); }
  };

  const handleFermerCaisse = async () => {
    if (montantCompte === '') { showCaisseToast('error', 'Veuillez saisir le montant compté'); return; }
    setSubmittingCaisse(true);
    try {
      const payload = { montantCompteCaisse: parseFloat(montantCompte) || 0, noteFermeture: noteCaisse };
      if (selectedBoutiqueId && selectedBoutiqueId !== 'all') payload.boutiqueId = selectedBoutiqueId;
      await fermerCaisse(payload);
      showCaisseToast('success', 'Caisse fermée avec succès');
      setCaisseModal(null); setMontantCompte(''); setNoteCaisse('');
      await loadCaisse();
    } catch (err) { showCaisseToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmittingCaisse(false); }
  };

  const loadConfigurations = async () => {
    try {
      const { data } = await getConfigurations();
      if (data.data) setConfig({ devise: data.data.devise || 'XOF', nom_entreprise: data.data.nom_entreprise || '' });
    } catch { /* ignore */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [statsRes, alertesRes, evolutionRes, topRes] = await Promise.all([
        getStats(),
        getAlertesStock(),
        getEvolutionVentes(parseInt(timeRange)),
        getTopProduits(parseInt(timeRange))
      ]);
      setStats(statsRes.data.data);
      setAlertes(alertesRes.data.data || []);
      setEvolution(evolutionRes.data.data || []);
      setTopProduits(topRes.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => {
    const code = config.devise || 'XOF';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: code, minimumFractionDigits: 0 }).format(n || 0);
  };
  const fmtK = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return String(Math.round(n || 0));
  };

  const trend = () => {
    if (evolution.length < 2) return { pct: 0, up: true };
    const last = evolution[evolution.length - 1]?.chiffreAffaires || 0;
    const prev = evolution[evolution.length - 2]?.chiffreAffaires || 0;
    if (!prev) return { pct: 0, up: true };
    const p = ((last - prev) / prev) * 100;
    return { pct: Math.abs(p).toFixed(1), up: p >= 0 };
  };

  const t = trend();

  if (loading) {
    return (
      <div className="db-loading">
        <Loader className="db-spinner" size={32} />
        <span>Chargement du tableau de bord…</span>
      </div>
    );
  }

  const variation = stats?.ventesJour?.variationVsHier;

  const kpis = [
    {
      label: "Ventes aujourd'hui",
      value: fmt(stats?.ventesJour?.chiffreAffaires),
      sub: `${stats?.ventesJour?.nombre || 0} vente${(stats?.ventesJour?.nombre || 0) !== 1 ? 's' : ''} · Encaissé ${fmt(stats?.ventesJour?.montantEncaisse)}`,
      icon: DollarSign,
      color: '#fca311',
      bg: '#fff8ec',
      trend: variation !== null && variation !== undefined
        ? { val: `${variation >= 0 ? '+' : ''}${variation.toFixed(1)}%`, up: variation >= 0 }
        : null
    },
    {
      label: 'CA ce mois',
      value: fmt(stats?.ventesMois?.chiffreAffaires),
      sub: `${stats?.ventesMois?.nombre || 0} transactions · Encaissé ${fmt(stats?.ventesMois?.montantEncaisse)}`,
      icon: TrendingUp,
      color: '#6366f1',
      bg: '#eef2ff',
      trend: null
    },
    {
      label: 'Dettes en cours',
      value: fmt(stats?.dettes?.total),
      sub: `${stats?.dettes?.nombre || 0} facture${(stats?.dettes?.nombre || 0) !== 1 ? 's' : ''} impayée${(stats?.dettes?.nombre || 0) !== 1 ? 's' : ''}`,
      icon: Users,
      color: '#f43f5e',
      bg: '#fff1f2',
      trend: null
    },
    {
      label: 'Alertes stock',
      value: String(alertes.length),
      sub: `${stats?.totalProduits || 0} produits · ${stats?.stock?.produitsRupture || 0} sous seuil`,
      icon: AlertTriangle,
      color: alertes.length > 0 ? '#ef4444' : '#10b981',
      bg: alertes.length > 0 ? '#fef2f2' : '#ecfdf5',
      trend: null
    }
  ];

  const caTotal = evolution.reduce((s, d) => s + (d.chiffreAffaires || 0), 0);

  return (
    <div className="db-page">

      {/* ── Top bar ── */}
      <div className="db-topbar">
        <div className="db-greeting">
          <div className="db-greeting-sub">
            {moment().format('dddd D MMMM YYYY')}
          </div>
          <h1 className="db-greeting-title">
            Bonjour{user?.prenom ? `, ${user.prenom}` : ''} 👋
          </h1>
          {config.nom_entreprise && (
            <p className="db-greeting-ent">{config.nom_entreprise}</p>
          )}
        </div>

        <div className="db-topbar-right">
          <div className="db-range-tabs">
            {[['7', '7j'], ['30', '30j'], ['90', '90j']].map(([v, lbl]) => (
              <button
                key={v}
                className={`db-range-tab ${timeRange === v ? 'active' : ''}`}
                onClick={() => setTimeRange(v)}
              >
                {lbl}
              </button>
            ))}
          </div>
          <button className="db-refresh-btn" onClick={load}>
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div className="db-kpi-row">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className="db-kpi-card" style={{ borderTopColor: k.color }}>
              <div className="db-kpi-top">
                <div className="db-kpi-icon" style={{ background: k.bg, color: k.color }}>
                  <Icon size={20} />
                </div>
                {k.trend && (
                  <div className={`db-kpi-trend ${k.trend.up ? 'up' : 'down'}`}>
                    {k.trend.up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
                    {k.trend.val}
                  </div>
                )}
              </div>
              <div className="db-kpi-value">{k.value}</div>
              <div className="db-kpi-label">{k.label}</div>
              <div className="db-kpi-sub">{k.sub}</div>
            </div>
          );
        })}
      </div>

      {/* ── Widget Caisse ── */}
      {caisse !== undefined && (
        <div className={`db-caisse-widget ${caisse ? 'db-caisse-widget--open' : 'db-caisse-widget--closed'}`}>
          <div className="db-caisse-widget-left">
            <div className={`db-caisse-widget-icon ${caisse ? 'db-caisse-widget-icon--open' : ''}`}>
              {caisse ? <Unlock size={18}/> : <Lock size={18}/>}
            </div>
            <div>
              <div className="db-caisse-widget-title">
                Caisse journalière
                <span className={`db-caisse-statut ${caisse ? 'db-caisse-statut--open' : 'db-caisse-statut--closed'}`}>
                  {caisse ? 'Ouverte' : 'Fermée'}
                </span>
              </div>
              {caisse ? (
                <div className="db-caisse-widget-stats">
                  <span><strong>{new Intl.NumberFormat('fr-FR').format(caisse.fondCaisse || 0)} {config.devise}</strong> fond</span>
                  <span className="db-caisse-sep">·</span>
                  <span><strong>{new Intl.NumberFormat('fr-FR').format(caisse.totalEspeces || 0)} {config.devise}</strong> espèces</span>
                  <span className="db-caisse-sep">·</span>
                  <span><strong style={{color:'#6366f1'}}>{new Intl.NumberFormat('fr-FR').format(caisse.especesAttendues || 0)} {config.devise}</strong> attendus</span>
                  {caisse.nombreVentes > 0 && <>
                    <span className="db-caisse-sep">·</span>
                    <span><strong>{caisse.nombreVentes}</strong> vente{caisse.nombreVentes > 1 ? 's' : ''}</span>
                  </>}
                  {caisse.caissier && <>
                    <span className="db-caisse-sep">·</span>
                    <span>{caisse.caissier.prenom} {caisse.caissier.nom}</span>
                  </>}
                </div>
              ) : (
                <div className="db-caisse-widget-sub">Aucune caisse ouverte pour aujourd'hui</div>
              )}
            </div>
          </div>
          <div className="db-caisse-widget-actions">
            <button className="db-caisse-widget-link" onClick={() => navigate('/caisse')}>
              <Landmark size={13}/> Détails
            </button>
            {caisse ? (
              <button className="db-caisse-btn db-caisse-btn--fermer"
                onClick={() => { setCaisseModal('fermer'); setMontantCompte(''); setNoteCaisse(''); }}>
                <Lock size={13}/> Fermer la caisse
              </button>
            ) : (
              <button className="db-caisse-btn db-caisse-btn--ouvrir"
                onClick={() => { setCaisseModal('ouvrir'); setFondCaisse(''); setNoteCaisse(''); }}>
                <Unlock size={13}/> Ouvrir la caisse
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Main grid ── */}
      <div className="db-main-grid">

        {/* ── Area chart ── */}
        <div className="db-card db-chart-card">
          <div className="db-card-head">
            <div>
              <div className="db-card-title">Évolution du chiffre d'affaires</div>
              <div className="db-card-sub">
                {timeRange} derniers jours · Total : <strong>{fmt(caTotal)}</strong>
              </div>
            </div>
            <div className="db-chart-legend">
              <span className="db-legend-dot" style={{ background: BRAND }} />
              <span>CA journalier</span>
            </div>
          </div>

          {evolution.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={evolution} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="caGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={BRAND} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={BRAND} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tickFormatter={d => moment(d).format('DD/MM')}
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={v => fmtK(v)}
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                  width={48}
                />
                <Tooltip
                  formatter={v => [fmt(v), 'CA']}
                  labelFormatter={d => moment(d).format('ddd D MMM')}
                  contentStyle={{
                    background: '#0f172a', color: 'white', border: 'none',
                    borderRadius: '8px', fontSize: '13px', padding: '10px 14px'
                  }}
                  labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                />
                <Area
                  type="monotone"
                  dataKey="chiffreAffaires"
                  stroke={BRAND}
                  strokeWidth={2.5}
                  fill="url(#caGrad)"
                  dot={false}
                  activeDot={{ r: 5, fill: BRAND, strokeWidth: 2, stroke: 'white' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="db-empty">
              <BarChart3 size={36} />
              <p>Aucune donnée sur {timeRange} jours</p>
            </div>
          )}
        </div>

        {/* ── Alertes stock ── */}
        <div className="db-card db-alerts-card">
          <div className="db-card-head">
            <div>
              <div className="db-card-title">Alertes stock</div>
              <div className="db-card-sub">{alertes.length} produit{alertes.length !== 1 ? 's' : ''} sous le seuil</div>
            </div>
            <button className="db-see-all" onClick={() => navigate('/stock')}>
              Voir tout <ChevronRight size={14} />
            </button>
          </div>

          {alertes.length === 0 ? (
            <div className="db-empty">
              <Package size={32} />
              <p>Tous les stocks sont OK</p>
            </div>
          ) : (
            <div className="db-alert-list">
              {alertes.slice(0, 6).map((a, i) => {
                const pct = Math.min(100, Math.round(((a.quantite || a.quantiteStock || 0) / (a.seuilAlerte || 1)) * 100));
                const color = pct === 0 ? '#ef4444' : pct < 50 ? '#f97316' : '#f59e0b';
                return (
                  <div key={a._id || i} className="db-alert-item">
                    <div className="db-alert-info">
                      <span className="db-alert-name">{a.nom}</span>
                      <span className="db-alert-cat">{a.codeProduit || '—'}</span>
                    </div>
                    <div className="db-alert-right">
                      <div className="db-alert-bar-wrap">
                        <div className="db-alert-bar" style={{ width: `${pct}%`, background: color }} />
                      </div>
                      <span className="db-alert-qty" style={{ color }}>
                        {a.quantite ?? a.quantiteStock ?? 0}
                        <span className="db-alert-min"> / {a.seuilAlerte}</span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom grid ── */}
      <div className="db-bottom-grid">

        {/* ── Top produits ── */}
        <div className="db-card">
          <div className="db-card-head">
            <div>
              <div className="db-card-title">Top produits</div>
              <div className="db-card-sub">Les plus vendus sur {timeRange} jours</div>
            </div>
          </div>

          {topProduits.length === 0 ? (
            <div className="db-empty">
              <Package size={32} />
              <p>Aucune donnée disponible</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  data={topProduits.slice(0, 5).map(p => ({
                    name: (p.produit?.nom || '').substring(0, 14),
                    ca: p.chiffreAffaires
                  }))}
                  margin={{ top: 4, right: 4, bottom: 4, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={44} />
                  <Tooltip
                    formatter={v => [fmt(v), 'CA']}
                    contentStyle={{ background: '#0f172a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px' }}
                  />
                  <Bar dataKey="ca" radius={[6, 6, 0, 0]}>
                    {topProduits.slice(0, 5).map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <div className="db-top-list">
                {topProduits.slice(0, 5).map((p, i) => (
                  <div key={i} className="db-top-item">
                    <span className="db-top-rank" style={{ background: PALETTE[i % PALETTE.length] + '22', color: PALETTE[i % PALETTE.length] }}>
                      #{i + 1}
                    </span>
                    <span className="db-top-name">{p.produit?.nom || '—'}</span>
                    <span className="db-top-qty">{p.quantiteVendue} pcs</span>
                    <span className="db-top-ca">{fmt(p.chiffreAffaires)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── Actions rapides ── */}
        <div className="db-card db-actions-card">
          <div className="db-card-head">
            <div className="db-card-title">Actions rapides</div>
          </div>

          <div className="db-actions-grid">
            {[
              { label: 'Nouvelle vente',   icon: ShoppingCart, color: '#fca311', bg: '#fff8ec', path: '/ventes/nouvelle' },
              { label: 'Ajouter produit',  icon: Package,      color: '#6366f1', bg: '#eef2ff', path: '/produits' },
              { label: 'Voir les dettes',  icon: Users,        color: '#ef4444', bg: '#fef2f2', path: '/dettes' },
              { label: 'Voir les ventes',  icon: BarChart3,    color: '#10b981', bg: '#ecfdf5', path: '/ventes' },
              { label: 'Caisse du jour',   icon: DollarSign,   color: '#f59e0b', bg: '#fffbeb', path: '/caisse' },
              { label: 'Factures',         icon: FileText,     color: '#0ea5e9', bg: '#f0f9ff', path: '/factures' },
              { label: 'Mouvement stock',  icon: Zap,          color: '#8b5cf6', bg: '#f5f3ff', path: '/stock' },
              { label: 'Configuration',    icon: Plus,         color: '#64748b', bg: '#f8fafc', path: '/configuration' },
            ].map((a, i) => {
              const Icon = a.icon;
              return (
                <button key={i} className="db-action-btn" onClick={() => navigate(a.path)}>
                  <div className="db-action-icon" style={{ background: a.bg, color: a.color }}>
                    <Icon size={20} />
                  </div>
                  <span className="db-action-label">{a.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Toast caisse ── */}
      {caisseToast && (
        <div className={`db-caisse-toast db-caisse-toast--${caisseToast.type}`}>
          {caisseToast.type === 'success' ? <CheckCircle size={14}/> : <AlertCircle size={14}/>}
          {caisseToast.text}
        </div>
      )}

      {/* ── Modal Ouvrir ── */}
      {caisseModal === 'ouvrir' && (
        <>
          <div className="db-modal-overlay" onClick={() => setCaisseModal(null)}/>
          <div className="db-modal">
            <div className="db-modal-head">
              <div>
                <h2 className="db-modal-title"><Unlock size={15}/> Ouvrir la caisse</h2>
                <p className="db-modal-sub">Caisse journalière — {new Date().toLocaleDateString('fr-FR')}</p>
              </div>
              <button className="db-modal-close" onClick={() => setCaisseModal(null)}><X size={16}/></button>
            </div>
            <div className="db-modal-body">
              <div className="db-modal-field">
                <label>Fond de caisse ({config.devise})</label>
                <input type="number" min="0" step="500" autoFocus placeholder="0"
                  value={fondCaisse} onChange={e => setFondCaisse(e.target.value)}/>
                <span className="db-modal-hint">Montant en espèces présent physiquement à l'ouverture</span>
              </div>
              <div className="db-modal-field">
                <label>Note d'ouverture <span className="db-modal-opt">(optionnel)</span></label>
                <input type="text" placeholder="Remarque…"
                  value={noteCaisse} onChange={e => setNoteCaisse(e.target.value)}/>
              </div>
            </div>
            <div className="db-modal-footer">
              <button className="db-modal-btn-cancel" onClick={() => setCaisseModal(null)}>Annuler</button>
              <button className="db-modal-btn-confirm db-modal-btn-confirm--open"
                onClick={handleOuvrirCaisse} disabled={submittingCaisse}>
                {submittingCaisse
                  ? <><Loader size={13} className="db-spin"/> Ouverture…</>
                  : <><Unlock size={13}/> Ouvrir</>}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Modal Fermer ── */}
      {caisseModal === 'fermer' && (
        <>
          <div className="db-modal-overlay" onClick={() => setCaisseModal(null)}/>
          <div className="db-modal">
            <div className="db-modal-head">
              <div>
                <h2 className="db-modal-title"><Lock size={15}/> Fermer la caisse</h2>
                <p className="db-modal-sub">Clôture journalière — {new Date().toLocaleDateString('fr-FR')}</p>
              </div>
              <button className="db-modal-close" onClick={() => setCaisseModal(null)}><X size={16}/></button>
            </div>
            <div className="db-modal-body">
              {caisse && (
                <div className="db-modal-caisse-recap">
                  <div className="db-modal-recap-row"><span>Espèces attendues</span><strong style={{color:'#6366f1'}}>{new Intl.NumberFormat('fr-FR').format(caisse.especesAttendues || 0)} {config.devise}</strong></div>
                  <div className="db-modal-recap-row"><span>Ventes espèces</span><strong>{new Intl.NumberFormat('fr-FR').format(caisse.totalEspeces || 0)} {config.devise}</strong></div>
                </div>
              )}
              <div className="db-modal-field">
                <label>Montant compté en caisse * ({config.devise})</label>
                <input type="number" min="0" step="500" autoFocus required
                  placeholder="Montant physiquement présent"
                  value={montantCompte} onChange={e => setMontantCompte(e.target.value)}/>
              </div>
              <div className="db-modal-field">
                <label>Note de fermeture <span className="db-modal-opt">(optionnel)</span></label>
                <input type="text" placeholder="Remarque…"
                  value={noteCaisse} onChange={e => setNoteCaisse(e.target.value)}/>
              </div>
            </div>
            <div className="db-modal-footer">
              <button className="db-modal-btn-cancel" onClick={() => setCaisseModal(null)}>Annuler</button>
              <button className="db-modal-btn-confirm db-modal-btn-confirm--close"
                onClick={handleFermerCaisse} disabled={submittingCaisse}>
                {submittingCaisse
                  ? <><Loader size={13} className="db-spin"/> Fermeture…</>
                  : <><Lock size={13}/> Fermer la caisse</>}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
